/**
 * Screen 16 — Snapshot Collector（快照采集页面）
 *
 * 访问: #/snapshot-collector
 * 自动运行 7 场景 headless 对打，生成 JSON 快照供 golden 对比。
 *
 * 产出: tools/test-harness/produced/scene-*.json
 */

import { pathFor } from "../router/screen-router";

// 内联 headless battle（复用 showcase-validator 中的 runHeadlessBattle 模式）
import { EngineScheduler } from "../engine/engine-scheduler";
import { getMapById } from "../engine/map-utils";
import { TANK_STATS } from "../engine/tank-stats";
import type { BattleState, EngineEvent } from "../engine/types";

// AI bot 代码 — 使用默认 aggressive ai
import defaultAiRaw from "../../../data/levels/_builtin/battle_easy/ai.py?raw";

interface TickSnapshot {
  tick: number;
  selfHp: number;
  enemyHp: number;
  selfAction: string;
  enemyAction: string;
  selfPos?: { x: number; y: number };
  enemyPos?: { x: number; y: number };
  damageAtoB: number;
  damageBtoA: number;
  cooldownA: number;
  cooldownB: number;
}

interface HeadlessResult {
  winner: "team_a" | "team_b" | "draw";
  totalTicks: number;
  ticks: TickSnapshot[];
  ttkTicks: number;
  damageAtoB: number;
  damageBtoA: number;
  error?: string;
}

function runHeadlessBattle(mapId: string, maxTicks: number, speedMultiplier: number, unitsPerTeam: number): Promise<HeadlessResult> {
  return new Promise((resolve) => {
    const engine = new EngineScheduler();
    engine.setLevelId(mapId);
    engine.setSpeedMultiplier(speedMultiplier);
    engine.setHeadlessMode(true);
    if (unitsPerTeam > 1) engine.setUnitsPerTeam(unitsPerTeam);

    const snapshots: TickSnapshot[] = [];
    let damageAtoB = 0;
    let damageBtoA = 0;
    let lastTick = 0;
    let ttkTicks = 0;
    let resolved = false;

    const finish = (result: Partial<HeadlessResult>) => {
      if (resolved) return;
      resolved = true;
      removeListener();
      engine.stop();
      resolve({
        winner: result.winner ?? "draw",
        totalTicks: result.totalTicks ?? lastTick,
        ticks: snapshots,
        ttkTicks: result.ttkTicks ?? ttkTicks,
        damageAtoB,
        damageBtoA,
        error: result.error,
      });
    };

    const removeListener = engine.on((event: EngineEvent) => {
      switch (event.type) {
        case "tick": {
          lastTick = event.tickNum;
          const s = event.state;
          snapshots.push({
            tick: event.tickNum,
            selfHp: s.self.health,
            enemyHp: s.enemy.health,
            selfAction: event.actions.team_a.type,
            enemyAction: event.actions.team_b.type,
            selfPos: { ...s.self.position },
            enemyPos: { ...s.enemy.position },
            damageAtoB: event.damage.team_a_to_team_b,
            damageBtoA: event.damage.team_b_to_team_a,
            cooldownA: s.self.cooldown_left,
            cooldownB: 0, // enemy cooldown not directly available from BattleState
          });
          if (event.damage.team_a_to_team_b > 0) damageAtoB += event.damage.team_a_to_team_b;
          if (event.damage.team_b_to_team_a > 0) damageBtoA += event.damage.team_b_to_team_a;
          // 检测 TTK: 敌方 HP 首次归零
          if (ttkTicks === 0 && s.enemy.health <= 0) {
            ttkTicks = event.tickNum;
          }
          break;
        }
        case "battle_end":
          finish({ winner: event.winner, totalTicks: lastTick, ttkTicks: ttkTicks || lastTick });
          break;
        case "error":
          finish({ winner: "draw", totalTicks: lastTick, error: "engine_error" });
          break;
      }
    });

    const map = getMapById(mapId);
    const dims = map.dimensions ?? { width: 25, height: 25 };
    const allSpawnsA = map.spawns?.team_a ?? [{ x: 0, y: 12 }];
    const allSpawnsB = map.spawns?.team_b ?? [{ x: 24, y: 12 }];

    const teamATanks: any[] = [];
    const teamBTanks: any[] = [];
    for (let i = 0; i < unitsPerTeam; i++) {
      const sa = allSpawnsA[i] ?? allSpawnsA[0];
      const sb = allSpawnsB[i] ?? allSpawnsB[0];
      teamATanks.push({
        team: "team_a", tank_index: i,
        position: { x: sa.x, y: sa.y }, spawn: { x: sa.x, y: sa.y },
        health: TANK_STATS.max_health, max_health: TANK_STATS.max_health,
        cooldown_left: 0, attack_cooldown_ticks: TANK_STATS.attack_cooldown_ticks,
        is_alive: true,
      });
      teamBTanks.push({
        team: "team_b", tank_index: i,
        position: { x: sb.x, y: sb.y }, spawn: { x: sb.x, y: sb.y },
        health: TANK_STATS.max_health, max_health: TANK_STATS.max_health,
        cooldown_left: 0, attack_cooldown_ticks: TANK_STATS.attack_cooldown_ticks,
        is_alive: true,
      });
    }

    const spawnB = allSpawnsB[0];
    const initialState: BattleState = {
      tick: 0,
      map_id: mapId,
      map_size: { width: dims.width, height: dims.height },
      self: { ...teamATanks[0] },
      enemy: {
        tank_index: 0,
        position: { x: spawnB.x, y: spawnB.y },
        spawn: { x: spawnB.x, y: spawnB.y },
        health: TANK_STATS.max_health, max_health: TANK_STATS.max_health,
        is_alive: true, is_visible: false, last_known_position: null,
      },
      nearest_cover: null,
      team_a: { tanks: teamATanks, units_per_team: unitsPerTeam },
      team_b: { tanks: teamBTanks, units_per_team: unitsPerTeam },
    };

    engine.start(defaultAiRaw, initialState, defaultAiRaw);
    setTimeout(() => finish({ winner: "draw", totalTicks: lastTick, error: "timeout" }), maxTicks * 50 + 5000);
  });
}

// 场景定义（与 tools/test-harness/scenes/ 同步）
const SCENES = [
  { id: "scene-01-basic-move", name: "基础移动验证", map: "battle_easy", maxTicks: 50, speed: 10, units: 1 },
  { id: "scene-02-cooldown", name: "冷却递减验证", map: "battle_easy", maxTicks: 100, speed: 10, units: 1 },
  { id: "scene-03-distance", name: "距离计算验证", map: "battle_easy", maxTicks: 60, speed: 10, units: 1 },
  { id: "scene-04-collision", name: "碰撞检测验证", map: "battle_easy", maxTicks: 80, speed: 10, units: 1 },
  { id: "scene-05-vision", name: "视野系统验证", map: "battle_easy", maxTicks: 120, speed: 10, units: 1 },
  { id: "scene-06-damage", name: "伤害计算验证", map: "battle_easy", maxTicks: 200, speed: 10, units: 1 },
  { id: "scene-07-ttk", name: "TTK 基准指标", map: "battle_easy", maxTicks: 500, speed: 10, units: 1 },
];

interface SceneResult {
  sceneId: string;
  name: string;
  ticks: TickSnapshot[];
  totalTicks: number;
  winner: string;
  ttkTicks: number;
  damageAtoB: number;
  damageBtoA: number;
  error?: string;
}

export function renderScreen16SnapshotCollector(
  app: HTMLElement,
  onNavigate: (path: string) => void
): void {
  app.innerHTML = `
    <div class="s16-root">
      <div class="s16-header">
        <h1 class="s16-title">Snapshot Collector</h1>
        <p class="s16-desc">Test Harness — 7 场景 headless 战斗快照采集，用于 Golden File 对比</p>
      </div>
      <div class="s16-controls">
        <button class="s16-btn s16-btn-all" id="s16-btn-all">Run All (7 scenes)</button>
        <button class="s16-btn s16-btn-export" id="s16-btn-export" disabled>Export JSON</button>
      </div>
      <div class="s16-status" id="s16-status">
        <p>点击 "Run All" 开始采集</p>
        <p class="s16-hint">采集完成后可导出 JSON 文件到 tools/test-harness/produced/</p>
      </div>
      <div class="s16-progress" id="s16-progress" hidden>
        <div class="s16-progress-bar" id="s16-progress-bar"></div>
        <span id="s16-progress-text">0/7</span>
      </div>
      <div class="s16-results" id="s16-results"></div>
      <button class="s16-back-btn" id="s16-back-btn">回到主页</button>
    </div>
  `;

  app.querySelector("#s16-back-btn")?.addEventListener("click", () => onNavigate(pathFor("armory-hall")));

  let lastResults: SceneResult[] = [];

  app.querySelector("#s16-btn-all")?.addEventListener("click", async () => {
    const statusEl = app.querySelector("#s16-status") as HTMLElement;
    const progressEl = app.querySelector("#s16-progress") as HTMLElement;
    const progressBar = app.querySelector("#s16-progress-bar") as HTMLElement;
    const progressText = app.querySelector("#s16-progress-text") as HTMLElement;
    const resultsEl = app.querySelector("#s16-results") as HTMLElement;
    const exportBtn = app.querySelector("#s16-btn-export") as HTMLButtonElement;
    const runBtn = app.querySelector("#s16-btn-all") as HTMLButtonElement;

    runBtn.disabled = true;
    exportBtn.disabled = true;
    progressEl.hidden = false;
    resultsEl.innerHTML = "";
    lastResults = [];

    for (let i = 0; i < SCENES.length; i++) {
      const scene = SCENES[i];
      statusEl.innerHTML = `<p>Running ${i + 1}/${SCENES.length}: ${scene.name}...</p>`;
      progressBar.style.width = `${((i) / SCENES.length) * 100}%`;
      progressText.textContent = `${i}/${SCENES.length}`;

      try {
        const result = await runHeadlessBattle(scene.map, scene.maxTicks, scene.speed, scene.units);
        const sceneResult: SceneResult = {
          sceneId: scene.id,
          name: scene.name,
          ticks: result.ticks,
          totalTicks: result.totalTicks,
          winner: result.winner,
          ttkTicks: result.ttkTicks,
          damageAtoB: result.damageAtoB,
          damageBtoA: result.damageBtoA,
          error: result.error,
        };
        lastResults.push(sceneResult);

        const icon = result.error ? "❌" : "✅";
        resultsEl.innerHTML += `
          <div class="s16-result-row ${result.error ? "s16-error" : "s16-ok"}">
            <span>${icon} ${scene.name}</span>
            <span>${result.totalTicks} ticks | winner=${result.winner} | TTK=${result.ttkTicks}</span>
          </div>
        `;
      } catch (e) {
        resultsEl.innerHTML += `
          <div class="s16-result-row s16-error">
            <span>❌ ${scene.name}</span>
            <span>Exception: ${(e as Error).message}</span>
          </div>
        `;
      }

      progressBar.style.width = `${((i + 1) / SCENES.length) * 100}%`;
      progressText.textContent = `${i + 1}/${SCENES.length}`;
    }

    runBtn.disabled = false;
    statusEl.innerHTML = `<p>采集完成！${lastResults.length}/${SCENES.length} 场景成功</p>`;
    exportBtn.disabled = lastResults.length === 0;

    // 暴露到全局，方便自动化脚本提取
    (window as any).__snapshotResults = lastResults;
    (window as any).__snapshotDone = true;
  });

  app.querySelector("#s16-btn-export")?.addEventListener("click", () => {
    if (lastResults.length === 0) return;

    // 逐个下载 JSON 文件
    for (const r of lastResults) {
      const golden = {
        sceneId: r.sceneId,
        name: r.name,
        createdAt: new Date().toISOString(),
        totalTicks: r.totalTicks,
        winner: r.winner,
        ttkTicks: r.ttkTicks,
        damageAtoB: r.damageAtoB,
        damageBtoA: r.damageBtoA,
        ticks: r.ticks,
      };

      const blob = new Blob([JSON.stringify(golden, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.sceneId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }

    const statusEl = app.querySelector("#s16-status") as HTMLElement;
    statusEl.innerHTML += `<p>📥 ${lastResults.length} 个 JSON 文件已下载。请移动到 tools/test-harness/produced/ 目录</p>`;
  });
}
